import React, { useEffect, useState } from "react";
import { Loader2, Play, CheckCircle2, AlertTriangle, GitCompareArrows, Sparkles } from "lucide-react";
import { Breadcrumb, GradientPanel } from "../components/common";
import api from "../lib/api";

const statusStyle = {
  "Matched": "text-emerald-600 bg-emerald-50",
  "Amount mismatch": "text-amber-600 bg-amber-50",
  "Missing in statement": "text-rose-600 bg-rose-50",
};

const Reconciliation = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => { try { const { data } = await api.get("/reconciliation"); setData(data); } catch {} finally { setLoading(false); } };
  useEffect(() => { load(); }, []);
  const run = async () => { setBusy(true); try { const { data } = await api.post("/reconciliation/run"); setData(data); } catch {} finally { setBusy(false); } };

  return (
    <div>
      <Breadcrumb items={[{ label: "Dashboard", path: "/a/dashboard" }, { label: "Supplier Matching" }]} />
      <GradientPanel>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-white"><GitCompareArrows size={20} /></div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Supplier Statement Matching</h1>
              <p className="text-sm text-gray-500 mt-1">The Reconciliation agent extracts supplier statements and matches them to your books (2-way / 3-way), flagging exceptions automatically.</p>
            </div>
          </div>
          <button onClick={run} disabled={busy} className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 px-4 py-2 rounded-lg transition">{busy ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />} Run reconciliation</button>
        </div>

        {loading ? <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-indigo-500" size={26} /></div> : (
          <>
            <div className="grid grid-cols-3 gap-3 mt-6">
              <Stat label="Documents" value={data.total} />
              <Stat label="Matched" value={data.matched} good />
              <Stat label="Exceptions" value={data.exceptions} warn />
            </div>
            {data.lines?.length > 0 && (
              <div className="mt-6 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-gray-500 border-b border-gray-100"><th className="py-2 font-semibold">Number</th><th className="py-2 font-semibold">Buyer</th><th className="py-2 font-semibold text-right">Books</th><th className="py-2 font-semibold text-right">Statement</th><th className="py-2 font-semibold">Status</th><th className="py-2 font-semibold">Note</th></tr></thead>
                  <tbody>
                    {data.lines.map((l, i) => (
                      <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="py-2 font-medium text-gray-800">{l.number}</td>
                        <td className="py-2 text-gray-600">{l.buyer}</td>
                        <td className="py-2 text-right text-gray-700">{l.booked?.toFixed(2)} {l.currency}</td>
                        <td className="py-2 text-right text-gray-700">{l.statement != null ? l.statement.toFixed(2) : "-"}</td>
                        <td className="py-2"><span className={`text-[11px] font-semibold px-2 py-0.5 rounded inline-flex items-center gap-1 ${statusStyle[l.status]}`}>{l.status === "Matched" ? <CheckCircle2 size={11} /> : <AlertTriangle size={11} />}{l.status}</span></td>
                        <td className="py-2 text-gray-400 text-xs">{l.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="mt-4 rounded-xl bg-gradient-to-br from-violet-50 to-fuchsia-50 border border-violet-100 p-4">
              <p className="text-[11px] font-bold text-violet-600 flex items-center gap-1.5 mb-1"><Sparkles size={12} /> AGENT NARRATIVE</p>
              <p className="text-sm text-gray-600">{data.narrative}</p>
            </div>
          </>
        )}
      </GradientPanel>
    </div>
  );
};

const Stat = ({ label, value, good, warn }) => (
  <div className="rounded-xl border border-gray-100 bg-white p-4">
    <p className="text-xs text-gray-400">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${good ? "text-emerald-600" : warn ? "text-amber-600" : "text-gray-900"}`}>{value}</p>
  </div>
);

export default Reconciliation;
